import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import React from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

import { useGetSitterProfile } from '../../../hooks/sitter/useGetSitterProfile';
import AboutHomeCard from '../../ClientPages/SitterProfile/components/AboutHomeCard';
import AboutSection from '../../ClientPages/SitterProfile/components/AboutSection';
import AvailabilityCalendar from '../../ClientPages/SitterProfile/components/AvailabilityCalendar';
import CancellationPolicy from '../../ClientPages/SitterProfile/components/CancellationPolicy';
import LocationMap from '../../ClientPages/SitterProfile/components/LocationMap';
import PhotoGallery from '../../ClientPages/SitterProfile/components/PhotoGallery';
import ProfileHeader from '../../ClientPages/SitterProfile/components/ProfileHeader';
import ReviewsList from '../../ClientPages/SitterProfile/components/ReviewsList';
import ServicesCard from '../../ClientPages/SitterProfile/components/ServicesCard';

import SitterHeader from '../SitterHeader/SitterHeader';

const SitterProfilePreview: React.FC = () => {
  const navigate = useNavigate();

  const { data: sitterData, isLoading } = useGetSitterProfile();

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!sitterData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Profile not found</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <SitterHeader />
      <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Preview Banner */}
      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 2,
          '& .MuiAlert-message': { flex: 1 },
          '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
        }}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate('/sitter-settings')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Edit Profile
          </Button>
        }
      >
        This is how clients see your profile
      </Alert>

      {/* Profile Header */}
      <ProfileHeader sitter={sitterData} onBook={() => {}} isOwnProfile />

      {/* Photo Gallery */}
      <PhotoGallery photos={sitterData.galleryImages} sitterName={sitterData.firstName} />

      {/* Two-column layout — identical to client view */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          mt: 3,
        }}
      >
        {/* Left Column — Main Content */}
        <Box sx={{ flex: { xs: '1', md: '0 0 65%' } }}>
          <AboutSection
            bio={sitterData.bio ?? 'No bio provided yet.'}
            sitterName={sitterData.firstName}
          />

          <AvailabilityCalendar
            availability={Object.fromEntries(
              sitterData.availability.map((a) => [a.date, a.isAvailable])
            )}
          />

          <ReviewsList reviews={[]} sitterName={sitterData.firstName} />
        </Box>

        {/* Right Column — Sidebar */}
        <Box sx={{ flex: { xs: '1', md: '0 0 calc(35% - 24px)' } }}>
          <ServicesCard
            services={sitterData.pricing.map((p) => ({
              name: p.service,
              description: '',
              price: p.priceMin,
              unit: 'night',
            }))}
            isOwnProfile
          />

          <AboutHomeCard
            sitterName={sitterData.firstName}
            acceptedPets={{
              preferences: sitterData.petPreferences ?? [],
            }}
            home={{
              hasFencedYard: sitterData.homeEnvironment?.includes('yard-with-fence') ?? false,
              allowsPetsOnFurniture:
                sitterData.homeEnvironment?.includes('allowed-on-furniture') ?? false,
            }}
          />
          {sitterData.lat && sitterData.lng && (
            <LocationMap
              location={sitterData.username ?? sitterData.firstName}
              coordinates={{ lat: sitterData.lat, lng: sitterData.lng }}
            />
          )}

          <CancellationPolicy
            policy={{
              type: 'Flexible',
              description:
                'Full refund if cancelled before 12:00 p.m. one day before the booking, 50% refund afterward.\n\nNo refund is payable if the booking is cancelled on or after the start date.',
            }}
          />
        </Box>
      </Box>
    </Container>
    </Box>
  );
};

export default SitterProfilePreview;
